import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { findAnswer, getSmallTalkReply, getSuggestedQuestions, ROLES } from "../chatbot/knowledgeBase";
import API_BASE_URL from "../config";
import dannyAvatar from "../assets/danny-avatar.png";
import "./ChatbotWidget.css";

const PUBLIC_PATHS = ["/", "/login", "/register", "/forgotpassword", "/pricing",
  "/about", "/contact", "/privacy", "/cookies", "/blog", "/changelog",
  "/roadmap", "/help", "/status", "/community", "/security", "/careers",
  "/press", "/partners", "/why-balsuite"];

function getUserRole() {
  try {
    const raw = localStorage.getItem("roles");
    if (!raw) return null;
    // handles both JSON array '["ROLE_ADMIN"]' and plain string 'ROLE_ADMIN'
    const parsed = raw.startsWith("[") ? JSON.parse(raw) : [raw];
    if (parsed.includes("ROLE_ADMIN")) return ROLES.ADMIN;
    if (parsed.includes("ROLE_MANAGER")) return ROLES.MANAGER;
    if (parsed.includes("ROLE_USER")) return ROLES.USER;
    return ROLES.USER;
  } catch {
    return null;
  }
}

function isLoggedIn() {
  return !!localStorage.getItem("token");
}

const BOT_NAME = "Danny";
const TYPING_DELAY = 600;

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [role, setRole] = useState(getUserRole);
  const [navbarColor, setNavbarColor] = useState("#080235");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Re-derive role whenever the route changes (handles post-login navigation)
  useEffect(() => {
    setRole(getUserRole());
  }, [location.pathname]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");
    if (!token || !userId) return;
    fetch(`${API_BASE_URL}/api/appearance/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => { if (d?.navbarColor) setNavbarColor(d.navbarColor); })
      .catch(() => { });
  }, [role]);

  const isPublic = PUBLIC_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith("/blog")
  );
  const visible = isLoggedIn() && !isPublic && !!role;

  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, typing, open]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!typing && open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [typing]);

  // Reset when opened fresh
  useEffect(() => {
    if (open && messages.length === 0) {
      const name = localStorage.getItem("name") || "there";
      setMessages([
        {
          from: "bot",
          text: `Hi ${name}! I'm ${BOT_NAME}, your BalSuite assistant.\n\nAsk me anything about the system — how to use a feature, where to find a page, or what to do next.`,
        },
      ]);
      setShowSuggestions(true);
    }
  }, [open, messages.length]);

  if (!visible) return null;

  const suggestions = getSuggestedQuestions(role);

  function addBotReply(text, link) {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages((prev) => [...prev, { from: "bot", text, link }]);
    }, TYPING_DELAY);
  }

  function handleSend(text) {
    const msg = (text || input).trim();
    if (!msg) return;

    setInput("");
    setShowSuggestions(false);
    setMessages((prev) => [...prev, { from: "user", text: msg }]);
    inputRef.current?.focus();

    const smallTalk = getSmallTalkReply(msg);
    if (smallTalk) {
      addBotReply(smallTalk.answer, null);
      if (smallTalk.showSuggestions) setShowSuggestions(true);
      return;
    }

    const entry = findAnswer(msg, role);
    if (entry) {
      addBotReply(entry.answer, entry.link);
    } else {
      addBotReply(
        "I'm not sure about that. Try rephrasing, or contact your Admin/HR for help.\n\nYou can also try one of the suggested topics below.",
        null
      );
      setShowSuggestions(true);
    }
  }

  function handleSuggestion(q) {
    handleSend(q);
  }

  function handleNavigate(link) {
    setOpen(false);
    navigate(link);
  }

  function handleClear() {
    setMessages([]);
    setShowSuggestions(true);
  }

  return (
    <>
      {/* Floating bubble */}
      <button
        className={`cbot-bubble ${open ? "cbot-bubble--active" : ""}`}
        style={{ background: navbarColor }}
        onClick={() => setOpen((o) => !o)}
        title="Danny — Help Assistant"
        aria-label="Open chatbot"
      >
        {open ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="cbot-panel">
          {/* Header */}
          <div className="cbot-header" style={{ background: navbarColor }}>
            <div className="cbot-header-left">
              <div className="cbot-avatar">
                <img src={dannyAvatar} alt="Danny" />
              </div>
              <div>
                <div className="cbot-header-title">{BOT_NAME}</div>
                <div className="cbot-header-sub">BalSuite Assistant</div>
              </div>
            </div>
            <div className="cbot-header-actions">
              <button className="cbot-icon-btn" onClick={handleClear} title="Clear chat">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 1 0 .49-3.34" />
                </svg>
              </button>
              <button className="cbot-icon-btn" onClick={() => setOpen(false)} title="Close">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="cbot-messages">
            {messages.map((m, i) => (
              <div key={i} className={`cbot-msg cbot-msg--${m.from}`}>
                {m.from === "bot" && (
                  <div className="cbot-msg-avatar">
                    <img src={dannyAvatar} alt="Danny" />
                  </div>
                )}
                <div className="cbot-msg-content">
                  <div className="cbot-msg-bubble">
                    {m.text.split("\n").map((line, li) => (
                      <span key={li}>
                        {line}
                        {li < m.text.split("\n").length - 1 && <br />}
                      </span>
                    ))}
                  </div>
                  {m.link && (
                    <button
                      className="cbot-nav-btn"
                      onClick={() => handleNavigate(m.link)}
                    >
                      Go there &rarr;
                    </button>
                  )}
                </div>
              </div>
            ))}

            {typing && (
              <div className="cbot-msg cbot-msg--bot">
                <div className="cbot-msg-avatar">
                  <img src={dannyAvatar} alt="Danny" />
                </div>
                <div className="cbot-typing">
                  <span /><span /><span />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Suggested questions */}
          {showSuggestions && !typing && (
            <div className="cbot-suggestions">
              <div className="cbot-suggestions-label">Suggested</div>
              <div className="cbot-suggestions-chips">
                {suggestions.map((q, i) => (
                  <button
                    key={i}
                    className="cbot-chip"
                    onClick={() => handleSuggestion(q)}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="cbot-input-row">
            <input
              ref={inputRef}
              className="cbot-input"
              type="text"
              placeholder="Ask me anything..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              disabled={typing}
            />
            <button
              className="cbot-send-btn"
              style={{ background: navbarColor }}
              onClick={() => handleSend()}
              disabled={!input.trim() || typing}
              title="Send"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
